import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import GameInfo from "../../components/GameInfo/GameInfo"; 
import Map from "../../components/Map/Map";
import Host from "../../components/Host/Host";
import LoadingOverlay from "../../components/LoadingOverlay/LoadingOverlay";
import Seo from "../../components/Seo/Seo";
import { useAlertDialog, useConfirmDialog } from "../../components/ConfirmDialog/ConfirmDialogProvider";
import { ROUTES, buildGamePath, buildProfilePath } from "../../utils/appRoutes";
import { buildAuthPathWithNext, getCurrentAppPath } from "../../utils/authRedirect";
import { formatDayFirstDate } from "../../utils/dateFormatting";
import { resolveParticipantTeamIndex } from "../../utils/gameSummary";
import resolveMediaUrl from "../../utils/resolveMediaUrl";
import "./GameDetails.css";

const API_URL = process.env.REACT_APP_API_URL || "";
const DEFAULT_SEO_IMAGE = "https://zealfc.com/logo512.png";

function normalizeStadiumText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/**
 * Helper function to find the user in the game's teams array.
 * Returns:
 *   teamIndex = 0 or 1 if found, else null
 *   playerData = the user object from that team, or null if not found
 */
function findUserInGame(game, userEmail) {
  if (!game || !game.teams) return { teamIndex: null, playerData: null };

  let foundTeamIndex = null;
  let foundPlayerData = null;

  // Check Team 0
  for (let i = 0; i < game.teams[0].length; i++) {
    const p = game.teams[0][i];
    if (p && p.email === userEmail) {
      foundTeamIndex = 0;
      foundPlayerData = p;
      break;
    }
  }

  if (foundPlayerData === null) {
    // Check Team 1
    for (let j = 0; j < game.teams[1].length; j++) {
      const p = game.teams[1][j];
      if (p && p.email === userEmail) {
        foundTeamIndex = 1;
        foundPlayerData = p;
        break;
      }
    }
  }

  return {
    teamIndex: resolveParticipantTeamIndex({
      matchEvents: game?.matchEvents,
      playerEmail: userEmail,
      fallbackTeamIndex: foundTeamIndex,
      attendance: foundPlayerData?.attendance,
    }),
    playerData: foundPlayerData,
  };
}

const GameDetails = ({ refreshTokens, handleLogout }) => {
  const { gameId } = useParams(); 
  const [game, setGame] = useState(null); 
  const [isSignedUp, setIsSignedUp] = useState(false); 
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null); 
  const [onWaitlist, setOnWaitlist] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const showAlert = useAlertDialog();
  const showConfirm = useConfirmDialog();
  const loginRedirectPath = buildAuthPathWithNext(ROUTES.LOGIN, getCurrentAppPath(location));
  const canonicalGamePath = buildGamePath(gameId);
  const parsedSeoDate = game?.date ? new Date(game.date) : null;
  const seoDateLabel =
    parsedSeoDate && !Number.isNaN(parsedSeoDate.getTime())
      ? formatDayFirstDate(parsedSeoDate, {
          weekday: "long",
          day: "numeric",
          month: "long",
        })
      : "";
  const seoTitle = game
    ? `${game.type ? `${game.type} at ` : "Football at "}${game.stadium?.name || "ZealFC"} | ZealFC`
    : "Game Details | ZealFC";
  const seoDescription = game
    ? `View details for ${game.type || "football"} at ${game.stadium?.name || "the stadium"}${
        game.stadium?.address ? `, ${game.stadium.address}` : ""
      }${seoDateLabel ? ` on ${seoDateLabel}` : ""}${game.time ? ` at ${game.time}` : ""}.`
    : "View football game details on ZealFC.";
  const seoImage = resolveMediaUrl(game?.stadium?.stadiumPicturePath, DEFAULT_SEO_IMAGE);
  const seoStructuredData = game
    ? {
        "@context": "https://schema.org",
        "@type": "SportsEvent",
        name: `${game.type || "Football"} at ${game.stadium?.name || "ZealFC"}`,
        description: seoDescription,
        image: [seoImage],
        ...(parsedSeoDate && !Number.isNaN(parsedSeoDate.getTime())
          ? { startDate: parsedSeoDate.toISOString() }
          : {}),
        ...(game.status === "upcoming"
          ? { eventStatus: "https://schema.org/EventScheduled" }
          : {}),
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: {
          "@type": "Place",
          name: game.stadium?.name || "ZealFC",
          ...(game.stadium?.address ? { address: game.stadium.address } : {}),
        },
        organizer: {
          "@type": "SportsOrganization",
          name: "ZealFC",
          url: "https://zealfc.com",
        },
      }
    : null;

  /******************************************************
   * Fetch the game + user data on mount or when gameId changes
   ******************************************************/
  useEffect(() => {
    fetchGameDetails({ showLoader: true });
    // eslint-disable-next-line
  }, [gameId]);

  const resetViewerState = () => {
    setUserEmail(null);
    setCurrentUserId(null);
    setIsSignedUp(false);
    setOnWaitlist(false);
  };

  const syncViewerState = (resolvedGame, userData) => {
    setUserEmail(userData.email);
    setCurrentUserId(userData?._id || null);

    const teams = Array.isArray(resolvedGame?.teams) ? resolvedGame.teams : [];
    const waitlist = Array.isArray(resolvedGame?.waitlist)
      ? resolvedGame.waitlist
      : [];

    const userInTeam = teams.some((team) =>
      Array.isArray(team) &&
      team.some((player) => player && player.email === userData.email)
    );
    setIsSignedUp(userInTeam);

    const userOnWaitlist = waitlist.some(
      (waitlistUser) => waitlistUser?.email === userData.email
    );
    setOnWaitlist(userOnWaitlist);
  };

  const runAuthedRequest = async (
    requestFn,
    {
      redirectToLoginOnMissingToken = false,
      redirectToLoginOnRefreshFailure = false,
    } = {}
  ) => {
    let idToken = localStorage.getItem("idToken");

    if (!idToken) {
      if (redirectToLoginOnMissingToken) {
        navigate(loginRedirectPath);
      }
      return null;
    }

    try {
      return await requestFn(idToken);
    } catch (error) {
      if (
        error.response?.status !== 401 ||
        typeof refreshTokens !== "function"
      ) {
        throw error;
      }

      const refreshed = await refreshTokens();
      if (!refreshed) {
        if (typeof handleLogout === "function") {
          handleLogout();
        }
        if (redirectToLoginOnRefreshFailure) {
          navigate(loginRedirectPath);
        }
        return null;
      }

      idToken = localStorage.getItem("idToken");
      if (!idToken) {
        if (redirectToLoginOnRefreshFailure) {
          navigate(loginRedirectPath);
        }
        return null;
      }

      return requestFn(idToken);
    }
  };

  const fetchViewerState = async (resolvedGame) => {
    try {
      const userResponse = await runAuthedRequest((idToken) =>
        axios.get(`${API_URL}/profile/user-profile`, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      );

      if (!userResponse) {
        resetViewerState();
        return;
      }

      syncViewerState(resolvedGame, userResponse.data);
    } catch (viewerError) {
      console.warn("Could not fetch authenticated viewer state:", viewerError);
      resetViewerState();
    }
  };

  const fetchGameDetails = async ({ showLoader = false } = {}) => {
    if (showLoader) {
      setLoading(true);
    }
    setError(null);

    try {
      // 1) Fetch the game
      const response = await axios.get(`${API_URL}/games/${gameId}`);
      let resolvedGame = response.data;

      // Fallback resolution for older games whose embedded stadium data
      // does not include amenities/type/surface.
      const embeddedStadium = resolvedGame?.stadium || {};
      const embeddedAmenities = Array.isArray(embeddedStadium.amenities)
        ? embeddedStadium.amenities.filter(Boolean)
        : [];
      const needsStadiumMeta =
        !embeddedStadium.type ||
        !embeddedStadium.surface ||
        embeddedAmenities.length === 0;

      if (needsStadiumMeta && embeddedStadium.name) {
        try {
          const stadiumsResponse = await axios.get(`${API_URL}/stadiums`);
          const stadiums = Array.isArray(stadiumsResponse.data)
            ? stadiumsResponse.data
            : [];

          const targetName = normalizeStadiumText(embeddedStadium.name);
          const targetAddress = normalizeStadiumText(embeddedStadium.address);

          const byNameAndAddress =
            targetAddress.length > 0
              ? stadiums.find(
                  (s) =>
                    normalizeStadiumText(s?.name) === targetName &&
                    normalizeStadiumText(s?.address) === targetAddress
                )
              : null;

          const byNameOnly = stadiums.find(
            (s) => normalizeStadiumText(s?.name) === targetName
          );

          const matchedStadium = byNameAndAddress || byNameOnly;

          if (matchedStadium) {
            resolvedGame = {
              ...resolvedGame,
              stadium: {
                ...embeddedStadium,
                amenities: Array.isArray(matchedStadium.amenities)
                  ? matchedStadium.amenities
                  : embeddedAmenities,
                type: embeddedStadium.type || matchedStadium.type || "",
                surface:
                  embeddedStadium.surface || matchedStadium.surface || "",
              },
            };
          }
        } catch (stadiumResolveError) {
          console.warn("Could not resolve stadium amenities fallback:", stadiumResolveError);
        }
      }

      setGame(resolvedGame);
      await fetchViewerState(resolvedGame);
    } catch (err) {
      console.error("Error fetching game details:", err);
      const statusCode = err?.response?.status;
      if (statusCode === 400 || statusCode === 404) {
        setError(null);
        setGame(null);
        resetViewerState();
        navigate(ROUTES.HOME, { replace: true });
        return;
      }

      setError(err);
      setGame(null);
      resetViewerState();
    } finally {
      setLoading(false);
    }
  };

  /******************************************************
   * Waitlist logic
   ******************************************************/
  const handleJoinWaitlist = async () => {
    try {
      const response = await runAuthedRequest(
        (idToken) =>
          axios.post(
            `${API_URL}/games/waitlist/${gameId}`,
            {},
            { headers: { Authorization: `Bearer ${idToken}` } }
          ),
        {
          redirectToLoginOnMissingToken: true,
          redirectToLoginOnRefreshFailure: true,
        }
      );
      if (!response) return;

      await showAlert({
        title: "WAITLIST",
        message: response.data.message,
      });
      setOnWaitlist(true);
      fetchGameDetails();
    } catch (error) {
      await showAlert({
        title: "WAITLIST",
        message: error.response?.data?.error || "Error joining waitlist.",
      });
    }
  };

  const handleLeaveWaitlist = async () => {
    try {
      const response = await runAuthedRequest(
        (idToken) =>
          axios.delete(`${API_URL}/games/waitlist/${gameId}`, {
            headers: { Authorization: `Bearer ${idToken}` },
          }),
        {
          redirectToLoginOnMissingToken: true,
          redirectToLoginOnRefreshFailure: true,
        }
      );
      if (!response) return;

      await showAlert({
        title: "WAITLIST",
        message: response.data.message,
      });
      setOnWaitlist(false);
      fetchGameDetails();
    } catch (error) {
      await showAlert({
        title: "WAITLIST",
        message: error.response?.data?.error || "Error leaving waitlist.",
      });
    }
  };

  /******************************************************
   * Signup logic
   ******************************************************/
  const handleSignup = async () => {
    if (!localStorage.getItem("idToken")) {
      navigate(loginRedirectPath);
      return;
    }

    const shouldJoin = await showConfirm({
      title: "JOIN GAME",
      message: "Please confirm you want to join this game.",
      confirmText: "Join",
      cancelText: "Cancel",
      cancelFirst: true,
      requireConsent: true,
      consentLabel: (
        <>
          I have read and accept the{" "}
          <a href={ROUTES.FACILITY_RULES} target="_blank" rel="noreferrer">
            ZealFC Facility &amp; Game Rules
          </a>
          .
        </>
      ),
    });

    if (!shouldJoin) {
      return;
    }

    try {
      const response = await runAuthedRequest(
        (idToken) =>
          axios.post(
            `${API_URL}/games/signup/${gameId}`,
            {},
            { headers: { Authorization: `Bearer ${idToken}` } }
          ),
        {
          redirectToLoginOnMissingToken: true,
          redirectToLoginOnRefreshFailure: true,
        }
      );
      if (!response) return;

      await showAlert({
        title: "GAME",
        message: response.data.message || "Signed up successfully for the game!",
      });
      setIsSignedUp(true);
      fetchGameDetails();
      navigate('/');
    } catch (error) {
      console.error("Error signing up for the game:", error);
      const msg = error.response?.data?.error || "Error signing up for the game.";
      await showAlert({
        title: "GAME",
        message: msg,
      });
      if (msg.toLowerCase().includes("not enough credits")) {
        navigate(
          buildProfilePath("subscription", { next: getCurrentAppPath(location) })
        );
      }
    }
  };

  /******************************************************
   * Cancel signup logic
   ******************************************************/
  const handleCancelSignup = async () => {
    const shouldCancel = await showConfirm({
      title: "CANCEL REGISTRATION",
      message: "Are you sure you want to cancel your registration for this game?",
      confirmText: "Yes",
      cancelText: "No",
    });

    if (!shouldCancel) {
      return;
    }

    try {
      const requestSucceeded = await runAuthedRequest(
        (idToken) =>
          axios.post(
            `${API_URL}/games/cancel-signup/${gameId}`,
            {},
            { headers: { Authorization: `Bearer ${idToken}` } }
          ),
        {
          redirectToLoginOnMissingToken: true,
          redirectToLoginOnRefreshFailure: true,
        }
      );
      if (!requestSucceeded) return;

      await showAlert({
        title: "GAME",
        message: "You have successfully canceled your signup for the game.",
      });
      setIsSignedUp(false);
      fetchGameDetails();
    } catch (error) {
      console.error("Error canceling signup for the game:", error);
      await showAlert({
        title: "GAME",
        message: error.response?.data?.error || "Error canceling your signup for the game.",
      });
    }
  };

  /******************************************************
   * Utility: Calculate places left
   ******************************************************/
  const calculatePlacesLeft = (game) => {
    if (!game || !game.teams) return 0;
    const totalSlots = game.teams.reduce((sum, team) => sum + team.length, 0);
    const occupiedSlots = game.teams.reduce(
      (sum, team) => sum + team.filter((player) => player !== null).length,
      0
    );
    return totalSlots - occupiedSlots;
  };
  const isGameFull = game ? calculatePlacesLeft(game) === 0 : false;

  /******************************************************
   * Render logic
   ******************************************************/
  if (loading) {
    return (
      <>
        <Seo
          title="Loading Game Details | ZealFC"
          description="Loading football game details on ZealFC."
          canonicalPath={canonicalGamePath}
        />
        <div className="game-details-container">
          <LoadingOverlay label="Loading game details" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Seo
          title="Game Details Unavailable | ZealFC"
          description="This football game could not be loaded right now."
          robots="noindex, nofollow"
          canonicalPath={canonicalGamePath}
        />
        <p>Error fetching game details: {error.response?.data?.error || error.message}</p>
      </>
    );
  }

  if (!game) {
    return (
      <>
        <Seo
          title="Game Not Found | ZealFC"
          description="The requested football game could not be found."
          robots="noindex, nofollow"
          canonicalPath={canonicalGamePath}
        />
        <p>Game not found.</p>
      </>
    );
  }

  // Identify the user's team index (0 or 1) and the player's data from the game doc
  let userTeamIndex = null;
  let userPlayerData = null;
  if (userEmail) {
    const { teamIndex, playerData } = findUserInGame(game, userEmail);
    userTeamIndex = teamIndex;
    userPlayerData = playerData;
  }

  // Format date
  const gameDate = formatDayFirstDate(game.date, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className="game-details-container">
      <Seo
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        canonicalPath={canonicalGamePath}
        structuredData={seoStructuredData}
      />
      <GameInfo
        time={game.time}
        date={gameDate}
        duration={game.duration}
        gameName={game.stadium?.name}
        tournament={game.type}
        stadiumAmenities={game.stadium?.amenities || []}
        stadiumType={game.stadium?.type || ""}
        stadiumSurface={game.stadium?.surface || ""}
        placesLeft={calculatePlacesLeft(game)}
        onSignUp={isSignedUp ? handleCancelSignup : handleSignup}
        isSignedUp={isSignedUp}
        onWaitlistToggle={onWaitlist ? handleLeaveWaitlist : handleJoinWaitlist}
        isWaitlisted={onWaitlist}
        isGameFull={isGameFull}
        gameStatus={game.status}
        gameOutcome={game.result?.outcome}
        gameResult={game.result}
        userTeamIndex={userTeamIndex}
        userPlayerData={userPlayerData}
        currentUserId={currentUserId}
        gameImagePath={game.stadium?.stadiumPicturePath}
        teams={game.teams}
        waitlist={game.waitlist}
        matchEvents={game.matchEvents || []}
        />

      {/* Map & Host remain the same */}
      <Map 
      address={game.stadium?.address}
      mapPath={game.stadium?.stadiumMapPath}
      gameName={game.stadium?.name}
      />
      <Host host={game.host} />
    </div>
  );
};

export default GameDetails;
